import axios from 'axios';
import { 
  ShadowInfo, 
  ShadowRequest, 
  OptimalTimeWindow,
  SessionCreate,
  SessionResponse,
  VineyardParameters
} from '../types';

// Add TypeScript interface for the window object with API_CONFIG
declare global {
  interface Window {
    API_CONFIG?: {
      BASE_URL: string;
    };
  }
}

// Define the optimal windows response type
interface OptimalWindowsResponse {
  location: {
    latitude: number;
    longitude: number;
  };
  message: string;
  optimal_windows: Array<{
    start: string;
    end: string;
    duration_minutes?: number;
  }>;
  parameters: VineyardParameters;
  timezone: string;
}

// Utility function to get the base URL
const getBaseUrl = (): string => {
    // Check for build-time environment variable (standard CRA)
    if (process.env.REACT_APP_API_BASE_URL) {
        console.log('Using REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
        return process.env.REACT_APP_API_BASE_URL;
    }
    
    // Fallback for local development if build-time var isn't set
    console.warn('REACT_APP_API_BASE_URL not set, defaulting to http://localhost:5000/api');
    return 'http://localhost:5000/api'; 
};

// Create an Axios instance
const api = axios.create({
    baseURL: getBaseUrl(), 
    withCredentials: true, // Important for handling cookies/sessions if needed
});

// Add auth token to requests if available
api.interceptors.request.use(config => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Log API requests for debugging
api.interceptors.request.use(
  config => {
    console.log(`API Request to ${config.url}:`, config);
    return config;
  },
  error => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Log API responses for debugging
api.interceptors.response.use(
  response => {
    console.log(`API Response from ${response.config.url}:`, response);
    return response;
  },
  error => {
    console.error('API Response Error:', error);
    return Promise.reject(error);
  }
);

// Session API calls
export const createSession = async (sessionData: { name: string }): Promise<{ id: string }> => {
  const response = await api.post<{ id: string }>(`/sessions`, sessionData);
  return response.data;
};

// Shadow API calls
export const calculateShadow = async (sessionId: string, shadowRequest: ShadowRequest): Promise<ShadowInfo> => {
  const response = await api.post<ShadowInfo>(`/shadows/calculate`, shadowRequest);
  return response.data;
};

export const getOptimalTimeWindow = async (sessionId: string, shadowRequest: Omit<ShadowRequest, "time">): Promise<OptimalTimeWindow> => {
  const response = await api.post<OptimalTimeWindow>(`/shadows/optimal-window`, shadowRequest);
  return response.data;
};

// Calculate optimal windows
export const calculateOptimalWindows = async (sessionId: string, request: Omit<ShadowRequest, "time">): Promise<OptimalWindowsResponse> => {
  const response = await api.post<OptimalWindowsResponse>(`/shadows/optimal-windows`, request);
  return response.data;
};

// Utility API calls
export const clearTimezoneCache = async (): Promise<{ status: string, message: string }> => {
  console.log('Clearing timezone cache...');
  const response = await api.post<{ status: string, message: string }>(`/utils/clear-timezone-cache`);
  console.log('Timezone cache cleared:', response.data);
  return response.data;
}; 